import { useCallback } from 'react';
import clsx from 'clsx';
import { Checkbox } from '@headlessui/react';

import { GroupedOption, SelectOption } from '~/shared/utils/types';
import { useXFetch } from '~/shared/utils/useXFetch';
import Typography from '~/shared/components/Typography';
import CheckIcon from '~/shared/assets/icons/check.svg';
import { useSearchFilter } from '~/shared/components/filters/search/common/useSearchFilter';
import SearchFilterBase from '~/shared/components/filters/search/common/SearchFilterBase';
import { DEBOUNCE_MS } from '~/shared/constants/polling';
import { useAuthContext } from '~/shared/context/useAuthContext';
import { appendOrgAccountsScope } from '~/shared/utils/organizationUtils';

interface AutoscaleRuleFromApi {
  ruleId: string;
  active: boolean;
  rule: unknown;
}

interface AutoscaleRulesAutoCompleteResponse {
  rules: AutoscaleRuleFromApi[];
}

export default function SearchRuleID({
  initialSelections,
  onSelectionsChange,
  className,
  placeholder = 'Search Rule ID',
  isDisclosureOpen = false,
}: {
  initialSelections: SelectOption[];
  onSelectionsChange: (selections: SelectOption[]) => void;
  className?: string;
  placeholder?: string;
  isDisclosureOpen?: boolean;
}) {
  const api = useXFetch();
  const { accounts, activeTenantId } = useAuthContext();

  const searchOptions = useCallback(
    async (pattern: string): Promise<GroupedOption[]> => {
      const trimmedPattern = pattern.trim();

      if (!trimmedPattern) {
        return [];
      }

      try {
        const filterParam = JSON.stringify({
          field: 'ruleId',
          value: trimmedPattern,
          op: 'contains',
        });
        const params = new URLSearchParams({ filters: filterParam });
        appendOrgAccountsScope(params, accounts, activeTenantId);
        const response = await api<AutoscaleRulesAutoCompleteResponse>(
          `/api/v1/volumes/autoscaling/rules?${params.toString()}`,
          { cache: 'no-store' }
        );
        const rules = response.rules || [];

        const groupedResults: GroupedOption[] =
          rules.length > 0
            ? [
                {
                  label: 'Rule ID',
                  options: rules.map((rule) => ({
                    id: rule.ruleId,
                    name: rule.ruleId,
                    group: 'ruleId' as const,
                  })),
                },
              ]
            : [];

        return groupedResults;
      } catch (error) {
        console.error('[RuleIdFilter] Error searching rule IDs:', error);
        return [];
      }
    },
    [api, accounts, activeTenantId]
  );

  const {
    searchResults,
    isLoading,
    inputValue,
    showResults,
    focusedIndex,
    localSelections,
    selectedIdSet,
    flattenedResults,
    inputRef,
    resultsRef,
    handleInputChange,
    handleInputFocus,
    handleInputBlur,
    handleSelect,
    handleResultsMouseDown,
    handleResultsMouseUp,
    handleKeyDown,
  } = useSearchFilter({
    initialSelections,
    onSelectionsChange,
    searchFunction: searchOptions,
    filterGroup: 'ruleId',
    debounceMs: DEBOUNCE_MS,
    defaultQuery: 'rule-',
    isDisclosureOpen,
  });

  const renderOption = useCallback(
    (option: SelectOption, isSelected: boolean, isFocused: boolean = false) => (
      <div
        key={option.id}
        className={clsx(
          'p-3 my-1 rounded-lg mx1 transition-colors flex items-center',
          isFocused && 'bg-primary-20 '
        )}
        onClick={() => handleSelect(option)}
      >
        <div className="mr-2 ">
          <Checkbox
            checked={isSelected}
            onClick={(e) => {
              e.stopPropagation();
              handleSelect(option);
            }}
            className={clsx(
              'group block size-5 p-[4px] rounded-md border border-gray-400 bg-white data-checked:bg-primary-500 ring:0 active:ring-primary-300 focus:ring-primary-500 cursor-pointer',
              isFocused && 'border-primary-400'
            )}
          >
            <CheckIcon className="text-white stroke-2 size-2.5 opacity-0 group-data-checked:opacity-100 transition duration-200 ease-in-out" />
          </Checkbox>
        </div>
        <Typography
          variant="p3-reg"
          className={clsx('text-primary-900 cursor-pointer', isSelected ? 'font-500' : 'font-400')}
        >
          {option.name}
        </Typography>
      </div>
    ),
    [handleSelect]
  );

  return (
    <div className={clsx('flex w-full flex-1 flex-col', className)}>
      <SearchFilterBase
        inputValue={inputValue}
        onInputChange={handleInputChange}
        inputRef={inputRef}
        placeholder={placeholder}
        searchResults={searchResults}
        isLoading={isLoading}
        showResults={showResults}
        focusedIndex={focusedIndex}
        flattenedResults={flattenedResults}
        localSelections={localSelections}
        onSelect={handleSelect}
        selectedIdSet={selectedIdSet}
        renderOption={renderOption}
        resultsRef={resultsRef}
        onResultsMouseDown={handleResultsMouseDown}
        onResultsMouseUp={handleResultsMouseUp}
        onKeyDown={handleKeyDown}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        className="w-full"
        mustTypeFirstMessage="Start typing rule ID"
      />
    </div>
  );
}
